import { motion } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import Accordion from '@/components/atom/Accordion';
import { GlassPanel } from '@/components/atom/GlassPanel';
import { StyledText } from '@/components/atom/Text';
import useEnv from '@/hooks/useEnv';
import { dashboardCardVariant } from '@/styles/motion';

const StyledGlassPanel = styled(GlassPanel)<{ $isActive?: boolean }>`
  height: fit-content;
  padding: 24px 0;

  & > * {
    opacity: ${(p) => (p.$isActive ? 1 : 0.4)};
    transition: linear 0.2s all;
  }

  &:hover {
    & > * {
      opacity: 1;
      cursor: pointer;
    }
  }

  & > button {
    opacity: ${(p) => (p.$isActive ? 1 : 0.4)};
  }

  &:hover {
    & > button {
      opacity: 1;
      cursor: pointer;
    }
  }

  ul {
    padding: 0 24px;
    margin-right: 4px;

    li + li {
      margin-top: 16px;
    }
  }
`;

interface FaqItem {
  question: string;
  answer: string;
}

export default function FAQPanel() {
  const { t } = useTranslation('earn');

  const { everdexLpRewardPoolPairSymbol } = useEnv();

  const [activeItems, setActiveItems] = useState<number[]>([]);

  const dict: FaqItem[] = useMemo(
    () =>
      t('faqList', {
        symbolPair: everdexLpRewardPoolPairSymbol,
        returnObjects: true,
      }),
    [everdexLpRewardPoolPairSymbol, t]
  );

  const handleClick = useCallback(
    (index: number) => () => {
      if (activeItems.includes(index)) {
        const newActiveItmes = activeItems.filter((v) => v !== index);
        setActiveItems(newActiveItmes);
      } else {
        setActiveItems((prev) => [...prev, index]);
      }
    },
    [activeItems]
  );

  return (
    <StyledGlassPanel
      as={motion.div}
      variants={dashboardCardVariant}
      $padding="xl"
      $flex
      $direction="column"
      $gap="xxl"
      $rounded="xl"
      $isActive={activeItems.length > 0}
    >
      <StyledText
        as="h2"
        $fontWeight="black"
        $fontSize="xl"
        style={{
          padding: '0 24px',
        }}
      >
        {t('faq')}
      </StyledText>
      <ul>
        {dict.map((item, index) => (
          <li key={index}>
            <Accordion
              title={item.question}
              content={item.answer}
              onClick={handleClick(index)}
            />
          </li>
        ))}
      </ul>
    </StyledGlassPanel>
  );
}
